import { useCallback, useLayoutEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * What the client has chosen so far.
 *
 * KEPT IN THE URL, not in memory. Three reasons, all of them things that go
 * wrong otherwise:
 *
 *  1. Back and forward have to work. A multi-step flow whose state lives in a
 *     context throws the client to the start on Back,
 *     and this is a phone surface where Back is the primary navigation.
 *  2. A refresh must not cost the entered choices. Mobile browsers reload
 *     backgrounded tabs aggressively.
 *  3. Product requires that on hold expiry we preserve completed details and
 *     clear ONLY the time. That is a one-parameter edit here, versus a careful
 *     partial reset of a state object.
 *
 * The HOLD is deliberately NOT here. It is server state with an owner and an
 * expiry; putting its id in a URL invites someone to share or bookmark a claim
 * on a slot that is not theirs.
 */
export interface FlowSelection {
  serviceId: string | null;
  /** `YYYY-MM-DD` in the PROFESSIONAL's timezone, which is where the day is. */
  date: string | null;
  /** ISO 8601 UTC start of the chosen slot. */
  startTime: string | null;
}

export function useFlowState({
  hideTime,
}: {
  /**
   * A time in the URL that must not be shown (rc.9 F4): a previous client's,
   * reached by Back. Asked during render, so no frame shows it; the caller
   * then removes it from the URL with a replace (`timeHidden`).
   */
  hideTime?: (time: string) => boolean;
} = {}) {
  const [params, setParams] = useSearchParams();

  const urlTime = params.get("time");
  const timeHidden = urlTime !== null && hideTime !== undefined && hideTime(urlTime);
  const selection: FlowSelection = {
    serviceId: params.get("service"),
    date: params.get("date"),
    startTime: timeHidden ? null : urlTime,
  };

  // STABLE CALLBACKS (IF-3/IF-10, rc.7). React Router's `setSearchParams` is
  // `useCallback(…, [navigate, searchParams])`: it changes identity on EVERY
  // URL change. `update`, and everything built on it, used to change with it,
  // so an effect that both changed the URL and listed one of these callbacks
  // as a dependency ran a second time (StepTime's expiry effect: two
  // refetches per expiry, proven 40/40). The latest setter is read through a
  // ref instead. A layout effect keeps it current before any passive effect
  // can call `update`, and its functional updater still sees the URL as of
  // the last commit, exactly as before.
  const setParamsRef = useRef(setParams);
  useLayoutEffect(() => {
    setParamsRef.current = setParams;
  }, [setParams]);

  const update = useCallback(
    (next: Partial<FlowSelection>, options?: { replace?: boolean }) => {
      setParamsRef.current(
        (current) => {
          const merged = new URLSearchParams(current);
          const write = (key: string, value: string | null | undefined) => {
            if (value) merged.set(key, value);
            else merged.delete(key);
          };
          if ("serviceId" in next) write("service", next.serviceId);
          if ("date" in next) write("date", next.date);
          if ("startTime" in next) write("time", next.startTime);
          return merged;
        },
        { replace: options?.replace ?? false }
      );
    },
    []
  );

  /**
   * Choosing a different service invalidates the day and time under it.
   *
   * A 30-minute slot is not a 90-minute slot: keeping the time would carry a
   * selection the professional's calendar may not have room for, and the client
   * would only find out at the hold. Cleared here rather than validated later.
   */
  const chooseService = useCallback(
    (serviceId: string) => update({ serviceId, date: null, startTime: null }),
    [update]
  );

  const chooseDate = useCallback((date: string) => update({ date, startTime: null }), [update]);

  const chooseTime = useCallback((startTime: string) => update({ startTime }), [update]);

  /**
   * Hold expired: clear the time, keep everything else.
   *
   * Product's rule verbatim — preserve the client's completed details, clear
   * only the selected time and hold, and return them to time selection.
   * `replace` so Back does not walk them into a URL still naming a slot that is
   * gone.
   */
  const clearExpiredTime = useCallback(() => update({ startTime: null }, { replace: true }), [update]);

  return { selection, timeHidden, chooseService, chooseDate, chooseTime, clearExpiredTime, update };
}
